import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import CallEndIcon from '@mui/icons-material/CallEnd';

/**
 * Modal de chamada de vídeo 1:1 com WebRTC.
 * Sinalização via Socket.IO (call:offer, call:answer, call:ice, call:hangup).
 */
export default function VideoCallModal({
  open,
  remoteUser,
  localStream,
  remoteStream,
  callState = 'connected',
  onClose,
  onHangup
}) {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);

  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  const toggleMute = () => {
    if (localStream) {
      const newMuted = !muted;
      localStream.getAudioTracks().forEach((t) => {
        t.enabled = !newMuted;
      });
      setMuted(newMuted);
    }
  };

  const toggleCamera = () => {
    if (localStream) {
      const newCameraOff = !cameraOff;
      localStream.getVideoTracks().forEach((t) => {
        t.enabled = !newCameraOff;
      });
      setCameraOff(newCameraOff);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgba(15,23,42,0.98)',
          border: '1px solid rgba(148,163,184,0.2)',
          borderRadius: 3
        }
      }}
    >
      <DialogTitle component="div" sx={{ color: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography component="span" variant="h6" sx={{ fontWeight: 600 }}>
          {callState === 'calling' ? 'Chamando...' : 'Chamada com '}{remoteUser?.nome || remoteUser?.email || 'Contato'}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: '#94a3b8' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', bgcolor: '#0f172a', width: 320, height: 240 }}>
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <Typography variant="caption" sx={{ position: 'absolute', bottom: 4, left: 4, color: '#fff' }}>
              Você
            </Typography>
          </Box>
          <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', bgcolor: '#1e293b', width: 320, height: 240 }}>
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <Typography variant="caption" sx={{ position: 'absolute', bottom: 4, left: 4, color: '#fff' }}>
              {remoteUser?.nome || 'Remoto'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
          <IconButton
            onClick={toggleMute}
            sx={{
              bgcolor: muted ? 'rgba(239,68,68,0.2)' : 'rgba(51,65,85,0.6)',
              color: '#e2e8f0',
              '&:hover': { bgcolor: muted ? 'rgba(239,68,68,0.3)' : 'rgba(51,65,85,0.8)' }
            }}
          >
            {muted ? <MicOffIcon /> : <MicIcon />}
          </IconButton>
          <IconButton
            onClick={toggleCamera}
            sx={{
              bgcolor: cameraOff ? 'rgba(239,68,68,0.2)' : 'rgba(51,65,85,0.6)',
              color: '#e2e8f0',
              '&:hover': { bgcolor: cameraOff ? 'rgba(239,68,68,0.3)' : 'rgba(51,65,85,0.8)' }
            }}
          >
            {cameraOff ? <VideocamOffIcon /> : <VideocamIcon />}
          </IconButton>
          <IconButton
            onClick={onHangup}
            sx={{
              bgcolor: 'rgba(239,68,68,0.3)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(239,68,68,0.5)' }
            }}
          >
            <CallEndIcon />
          </IconButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
